import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {
  useUserContext,
  ZAS
} from "../../../context";
import { useEffect, useRef, useState } from "react";
import { GeometryEditor, getLineStrings, getPolygon, googleSateliteHybrid, HidroLayer, HidroLayerGroup, HidroMap } from "../../mapa";
import { divIcon, LatLng, LayerGroup, Marker, point, Polygon, Polyline } from "leaflet";
import {
  AddHomeWork,
  ArrowDropDown,
  ArrowDropUp,
  Save,
} from "@mui/icons-material";
import { generateRandomHash } from "../../../core";
import { Cadastro, PolyZas, PolyZss, PontoEncontro, RotaAlternativa, RotaAlternativaData, RotaFuga, RotaFugaData, Sirene, UnidadeSensivel } from "../../zas";
import { TableUnidadesSensiveis } from "../unidadesSensiveisTable";
import { getValue, MatchProperties, PropertieType } from "./matchProperties";
import { SirenesTable } from "../sirenesTable";
import { PontosEncontroTable } from "../postosEncontroTable";
import { CadastrosTable } from "../cadastrosTable";
import { TableAcomodacoes } from "../acomodacoesTable";
import { BeforeUnload } from "../../../context/UnloadEventContext";

type SelectGeomType = "poly_zas" | "poly_zss" | "rota_fuga" | "rotas_alternativas";
interface ZasConfigProps {
  onChange: (zas: ZAS) => void;
  zas: ZAS;
}

export const ZasConfig: React.FC<ZasConfigProps> = ({ onChange, zas }) => {
  const theme = useTheme();
  const user = useUserContext();

  const layers = useRef(new HidroLayerGroup({
    name: "Layers",
    showing: true,
    layers: [new HidroLayer({
      name: "Mapa",
      layer: googleSateliteHybrid(),
      showing: true,
    })],
  }));

  const [_zas, setZas] = useState(zas);
  const [showEstruturas, setShowEstruturas] = useState(false);
  const [mapKey, setMapKey] = useState(generateRandomHash(8));
  const [showAcomodacoes, setShowAcomodacoes] = useState(false);
  const [openSirenesTable, setOpenSirenesTable] = useState(false);
  const [openCadastrosTable, setOpenCadastrosTable] = useState(false);
  const [layersRotaFuga, setLayersRotaFuga] = useState<HidroLayer[]>([]);
  const [openUniSensivelTable, setOpenUniSensivelTable] = useState(false);
  const [openPontosEncontroTable, setOpenPontosEncontroTable] = useState(false);
  const [geomEditing, setGeomEditing] = useState<HidroLayerGroup | undefined>();
  const [selectGeomType, setSelectGeomType] = useState<SelectGeomType | undefined>();
  const [layersRotasAlternativas, setLayersRotasAlternativas] = useState<HidroLayer[]>([]);
  const [propertiesRotasFuga, setPropertiesRotasFuga] = useState<{ [key: string]: PropertieType[]; }>();
  const [propertiesRotasAlternativas, setPropertiesRotasAlternativas] = useState<{ [key: string]: PropertieType[]; }>();
  const [centerGeom, setCenterGeom] = useState<[string | number, string | number]>([zas.latCenter ?? 0, zas.lonCenter ?? 0]);

  // Atualizar dados
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     onChange(_zas);
  //   }, 500);
  //   return () => clearTimeout(timer);
  // }, [_zas]);

  // Geometrias da ZAS
  useEffect(() => {
    // Centro do Município
    if (_zas.latCenter && _zas.lonCenter) {
      const layer = new HidroLayer({
        name: "Centro do Município",
        showing: true,
        layer: new Marker(new LatLng(_zas.latCenter, _zas.lonCenter)),
      });
      layer.setStyle({ iconStr: "Adjust", color: "#f00" });
      layers.current.push(layer);
    }

    // ZAS
    if (_zas.polygon && !("error" in _zas.polygon)) {
      const layer = new HidroLayer({
        name: "ZAS",
        showing: true,
        layer: _zas.polygon,
        properties: _zas.polygon.properties,
      });
      layers.current.push(layer);
    }

    // ZSS
    if (_zas.polygonZss && !("error" in _zas.polygonZss)) {
      const layer = new HidroLayer({
        name: "ZSS",
        showing: true,
        layer: _zas.polygonZss,
        properties: _zas.polygonZss.properties,
      });
      layers.current.push(layer);
    }

    // Pontos de Encontro
    if (_zas.pontosEncontro && !("error" in _zas.pontosEncontro)) {
      const layersAdd: HidroLayer[] = [];
      for (const ponto of _zas.pontosEncontro) {
        ponto.properties.idZas = _zas.id;

        layersAdd.push(new HidroLayer({
          name: ponto.properties.ponto,
          showing: true,
          layer: ponto,
          style: ponto.paintOptions,
        }));
      }

      layers.current.push(new HidroLayerGroup({
        name: "Pontos de Encontro",
        layers: layersAdd,
        showing: true,
      }));
    }

    // Sirenes
    if (_zas.sirenes && !("error" in _zas.sirenes)) {
      const layersAdd: HidroLayerGroup[] = [];
      let i = 1;
      for (const sirene of _zas.sirenes) {
        const layAdd: HidroLayer[] = [];
        if (sirene.properties.areaCobertura) {
          layAdd.push(new HidroLayer({
            name: `Área de Cobertura ${i}`,
            layer: sirene.properties.areaCobertura,
            showing: true,
            style: sirene.properties.areaCobertura.style,
          }));
        }

        layAdd.push(new HidroLayer({
          name: `Sirene ${i}`,
          showing: true,
          layer: sirene,
          style: sirene.style,
        }));

        layersAdd.push(new HidroLayerGroup({
          name: `Sirene ${i}`,
          showing: true,
          layers: layAdd,
        }));

        i++;
      }

      layers.current.push(new HidroLayerGroup({
        layers: layersAdd,
        name: "Sirenes",
        showing: true,
      }));
    }

    // Rotas de Fuga
    if (_zas.rotasFuga && !("error" in _zas.rotasFuga)) {
      layers.current.push(new HidroLayerGroup({
        name: "Rotas de Fuga",
        showing: true,
        layers: _zas.rotasFuga.map((rota) => new HidroLayer({
          name: rota.properties.nome,
          showing: true,
          layer: rota,
          properties: rota.properties,
        })),
      }));
    }

    // Acomodações
    if (_zas.acomodacoes && !("error" in _zas.acomodacoes)) {
      layers.current.push(new HidroLayerGroup({
        name: "Acomodações",
        showing: true,
        layers: _zas.acomodacoes.map(layer => new HidroLayer({
          name: layer.properties.nome,
          showing: true,
          layer: layer,
          style: layer.style || { iconStr: "Hotel", color: "#583506", iconSize: 25 },
        })),
      }));
    }

    // Rotas de Alternativas
    if (_zas.rotasAlternativas && !("error" in _zas.rotasAlternativas)) {
      layers.current.push(new HidroLayerGroup({
        name: "Rotas Alternativas",
        showing: true,
        layers: _zas.rotasAlternativas.map((rota) => new HidroLayer({
          name: rota.properties.nome,
          showing: true,
          layer: rota,
          properties: rota.properties,
        })),
      }));
    }

    // Unidades Sensíveis
    if (_zas.uniSensivel) {
      layers.current.push(getUnidadeSensivelLayer(_zas.uniSensivel));
    }

    // Cadastros
    if (_zas.cadastros && !("error" in _zas.cadastros)) {
      const layersAdd: HidroLayer[] = [];
      let i = 1;
      for (const cadastro of _zas.cadastros) {
        layersAdd.push(new HidroLayer({
          name: i.toString(),
          showing: true,
          layer: cadastro,
          style: cadastro.paintOptions,
        }));

        i++;
      }

      layers.current.push(new HidroLayerGroup({
        layers: layersAdd,
        name: "Cadastros",
        showing: true,
      }));
    }

  }, []);

  // Alterar a key do gráfico
  useEffect(() => {
    setTimeout(() => {
      if (showEstruturas) setMapKey(generateRandomHash(8));
    }, 500);
  }, [showEstruturas]);

  useEffect(() => {
    if (typeof centerGeom[0] === "string" || typeof centerGeom[1] === "string") return;

    for (const layer of layers.current.layers) {
      if (layer.name === "Centro do Município") {
        layers.current.removeLayer(layer);
      }
    }

    const layer = new HidroLayer({
      name: "Centro do Município",
      showing: true,
      layer: new Marker(new LatLng(centerGeom[0], centerGeom[1])),
    });
    layer.setStyle({ iconStr: "Adjust", color: "#f00" });
    layers.current.push(layer);

    setZas({ ..._zas, latCenter: centerGeom[0], lonCenter: centerGeom[1] });

  }, [centerGeom]);


  const onSelectGeometry = (geom: HidroLayerGroup) => {
    geom = geom.copy();

    setGeomEditing(undefined);
    setSelectGeomType(undefined);

    if (selectGeomType === "poly_zas") {
      const layer = getPolygon(geom.layers, true);

      for (const layer of layers.current.layers) {
        if (layer.name === "ZAS") {
          layers.current.removeLayer(layer);
        }
      }

      if (!layer) {
        setZas({ ..._zas, polygon: undefined });
        return;
      }

      let polyZas: PolyZas;
      if (layer.layer instanceof LayerGroup) {
        polyZas = new PolyZas(
          layer.layer.getLayers().map(layer => (layer as Polygon).getLatLngs() as L.LatLngExpression[][]),
          layer.properties,
          theme?.palette.geometries.polyZas
        );
      } else {
        polyZas = new PolyZas(
          (layer.layer as Polygon).getLatLngs(),
          layer.properties,
          theme?.palette.geometries.polyZas
        );
      }
      setZas({ ..._zas, polygon: polyZas });

      layers.current.push(
        new HidroLayer({
          name: "ZAS",
          showing: true,
          layer: polyZas,
          properties: polyZas.properties,
        })
      );
    }

    if (selectGeomType === "poly_zss") {
      const layer = getPolygon(geom.layers, true);

      for (const layer of layers.current.layers) {
        if (layer.name === "ZSS") {
          layers.current.removeLayer(layer);
        }
      }

      if (!layer) {
        setZas({ ..._zas, polygonZss: undefined });
        return;
      }

      let polyZss: PolyZss;
      if (layer.layer instanceof LayerGroup) {
        polyZss = new PolyZss(
          layer.layer.getLayers().map(layer => (layer as Polygon).getLatLngs() as L.LatLngExpression[][]),
          layer.properties,
          theme?.palette.geometries.polyZss
        );
      } else {
        polyZss = new PolyZss(
          (layer.layer as Polygon).getLatLngs(),
          layer.properties,
          theme?.palette.geometries.polyZss
        );
      }
      setZas({ ..._zas, polygonZss: polyZss });

      layers.current.push(
        new HidroLayer({
          name: "ZSS",
          showing: true,
          layer: polyZss,
          properties: polyZss.properties,
        })
      );
    }

    if (selectGeomType === "rota_fuga") {
      const layers = getLineStrings(geom.layers);
      const properties: { [key: string]: PropertieType[]; } = {};

      for (const layer of layers) {
        if (!layer.properties) continue;
        for (const key in layer.properties) {
          if (!(key in properties)) {
            properties[key] = [];
          }
          let type = typeof layer.properties[key];
          if (layer.properties[key] === null) type = "undefined";
          if (properties[key].includes(type)) continue;

          properties[key].push(type);
        }
      }
      setLayersRotaFuga(layers);
      setPropertiesRotasFuga(properties);
    }

    if (selectGeomType === "rotas_alternativas") {
      const layers = getLineStrings(geom.layers);
      const properties: { [key: string]: PropertieType[]; } = {};

      for (const layer of layers) {
        if (!layer.properties) continue;
        for (const key in layer.properties) {
          if (!(key in properties)) {
            properties[key] = [];
          }
          let type = typeof layer.properties[key];
          if (layer.properties[key] === null) type = "undefined";
          if (properties[key].includes(type)) continue;

          properties[key].push(type);
        }
      }
      setLayersRotasAlternativas(layers);
      setPropertiesRotasAlternativas(properties);
    }
  };

  const handleSelectUnidadesSensiveis = (unidades: UnidadeSensivel[]) => {
    for (const layer of layers.current.layers) {
      if (layer.name === "Unidades Sensíveis") {
        layers.current.removeLayer(layer);
      }
    }

    layers.current.push(getUnidadeSensivelLayer(unidades));
    setZas({ ..._zas, uniSensivel: unidades });
  };

  const handleSelectSirenes = (sirenes: Sirene[]) => {
    for (const layer of layers.current.layers) {
      if (layer.name === "Sirenes") {
        layers.current.removeLayer(layer);
      }
    }

    const layersAdd: HidroLayerGroup[] = [];
    let i = 1;
    for (const sirene of sirenes) {
      const layAdd: HidroLayer[] = [];
      if (sirene.properties.areaCobertura) {
        layAdd.push(new HidroLayer({
          name: `Área de Cobertura ${i}`,
          layer: sirene.properties.areaCobertura,
          showing: true,
          style: sirene.properties.areaCobertura.style,
        }));
      }

      layAdd.push(new HidroLayer({
        name: `Sirene ${i}`,
        showing: true,
        layer: sirene,
        style: sirene.style,
      }));

      layersAdd.push(new HidroLayerGroup({
        name: `Sirene ${i}`,
        showing: true,
        layers: layAdd,
      }));

      i++;
    }

    layers.current.push(new HidroLayerGroup({
      layers: layersAdd,
      name: "Sirenes",
      showing: true,
    }));
    setZas({ ..._zas, sirenes: sirenes });
  };

  const handlePontosEncontro = (pontos: PontoEncontro[]) => {
    for (const layer of layers.current.layers) {
      if (layer.name === "Pontos de Encontro") {
        layers.current.removeLayer(layer);
      }
    }

    const layersAdd: HidroLayer[] = [];
    for (const ponto of pontos) {
      ponto.properties.idZas = _zas.id;

      layersAdd.push(new HidroLayer({
        name: ponto.properties.ponto,
        showing: true,
        layer: ponto,
        style: ponto.paintOptions,
      }));
    }

    layers.current.push(new HidroLayerGroup({
      layers: layersAdd,
      name: "Pontos de Encontro",
      showing: true,
    }));
    setZas({ ..._zas, pontosEncontro: pontos });
  };

  const handleCadastros = (cadastros: Cadastro[]) => {
    for (const layer of layers.current.layers) {
      if (layer.name === "Cadastros") {
        layers.current.removeLayer(layer);
      }
    }

    const layersAdd: HidroLayer[] = [];
    let i = 1;
    for (const cadastro of cadastros) {
      cadastro.properties.idZas = _zas.id;

      layersAdd.push(new HidroLayer({
        name: i.toString(),
        showing: true,
        layer: cadastro,
        style: cadastro.paintOptions,
      }));

      i++;
    }

    layers.current.push(new HidroLayerGroup({
      layers: layersAdd,
      name: "Cadastros",
      showing: true,
    }));
    setZas({ ..._zas, cadastros: cadastros });
  };

  const handleRotasDeFuga = (matches: { [key: string]: string[]; }) => {
    setPropertiesRotasFuga(undefined);

    for (const layer of layers.current.layers) {
      if (layer.name === "Rotas de Fuga") {
        layers.current.removeLayer(layer);
      }
    }

    const rotas: RotaFuga[] = [];
    for (const layer of layersRotaFuga) {
      const properties: RotaFugaData = {
        id: 0,
        nome: "",
        idZas: _zas.id,
        obs: undefined,
        nivelValidade: 0,
        tempoChegada: 0,
        tempoSaida: undefined,
        comprimento: undefined,
        dataCadastro: new Date(Date.now()),
      };

      for (const key in properties) {
        if (key === "nome") {
          properties[key] = getValue(layer.properties ?? {}, matches['* Nome'], undefined, "");
        } else if (key === "obs") {
          properties[key] = getValue(layer.properties ?? {}, matches['Observações']);
        } else if (key === "nivelValidade") {
          properties[key] = getValue(layer.properties ?? {}, matches['* Nível de Validade'], undefined, 0);
        } else if (key === "tempoChegada") {
          properties[key] = getValue(layer.properties ?? {}, matches['* Tempo de Chegada'], undefined, 0);
        } else if (key === "tempoSaida") {
          properties[key] = getValue(layer.properties ?? {}, matches['Tempo de Saída']);
        } else if (key === "comprimento") {
          properties[key] = getValue(layer.properties ?? {}, matches['Comprimento da Rota']);
        }
      }

      const gjson = layer.toGeoJson();
      if (!gjson) continue;

      rotas.push(new RotaFuga(
        gjson.geometry as (GeoJSON.LineString | GeoJSON.MultiLineString),
        properties,
        layer.style ?? theme.palette.geometries.rotaFuga
      ));
    }

    setZas({ ..._zas, rotasFuga: rotas });
    layers.current.push(new HidroLayerGroup({
      name: "Rotas de Fuga",
      showing: true,
      layers: rotas.map((rota) => new HidroLayer({
        name: rota.properties.nome,
        showing: true,
        layer: rota,
      })),
    }));
  };

  const handleRotasAlternativas = (matches: { [key: string]: string[]; }) => {
    setPropertiesRotasAlternativas(undefined);

    for (const layer of layers.current.layers) {
      if (layer.name === "Rotas Alternativas") {
        layers.current.removeLayer(layer);
      }
    }

    const rotas: RotaAlternativa[] = [];
    for (const layer of layersRotasAlternativas) {
      const properties: RotaAlternativaData = {
        id: 0,
        nome: "",
        idZas: _zas.id,
        distanciaPercorrida: 0,
        tempoDeslocamento: 0,
      };

      for (const key in properties) {
        if (key === "nome") {
          properties[key] = getValue(layer.properties ?? {}, matches['Nome'], undefined, "");
        } else if (key === "distanciaPercorrida") {
          properties[key] = getValue(layer.properties ?? {}, matches['Distância Percorrida'], undefined, 0);
        } else if (key === "tempoDeslocamento") {
          properties[key] = getValue(layer.properties ?? {}, matches['Tempo de Deslocamento'], undefined, 0);
        }
      }

      const gjson = layer.toGeoJson();
      if (!gjson) continue;

      rotas.push(new RotaAlternativa(
        gjson.geometry as (GeoJSON.LineString | GeoJSON.MultiLineString),
        properties,
        layer.style ?? theme.palette.geometries.rotaFuga
      ));
    }

    setZas({ ..._zas, rotasAlternativas: rotas });
    layers.current.push(new HidroLayerGroup({
      name: "Rotas Alternativas",
      showing: true,
      layers: rotas.map((rota) => {
        const layer = new HidroLayer({
          name: rota.properties.nome,
          showing: true,
          layer: rota,
        });
        layer.setStyle(theme.palette.geometries.rotaAlternativa);

        return layer;
      }),
    }));
  };

  const handleChange = () => {
    onChange(_zas);
  };

  return <>
    {/* Modal Geometry Editor */}
    < GeometryEditor
      key={generateRandomHash(8)}
      open={Boolean(selectGeomType)}
      initialLayers={geomEditing?.copy()}
      onFinish={onSelectGeometry}
      onClose={() => {
        setGeomEditing(undefined);
        setSelectGeomType(undefined);
      }}
    />

    {/* Acomodações */}
    <Dialog
      fullWidth
      maxWidth={false}
      open={showAcomodacoes}
      onClose={() => setShowAcomodacoes(false)}
    >
      <DialogContent>
        <Box sx={{
          height: "100%",
          display: "flex",
          minHeight: "75vh",
          flexDirection: "column",
        }}>
          <TableAcomodacoes editable={user && !("error" in user) && user.admin} />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions>
        <Button onClick={() => setShowAcomodacoes(false)}>
          Finalizar
        </Button>
      </DialogActions>
    </Dialog>

    {/* Unidades sensíveis na ZAS */}
    <Dialog
      fullWidth
      maxWidth={false}
      open={openUniSensivelTable}
      onClose={() => setOpenUniSensivelTable(false)}
    >
      <DialogContent>
        <Box sx={{
          minHeight: "75vh",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}>
          <TableUnidadesSensiveis
            isFiltrable
            isAdd={user && !("error" in user) && user.admin}
            isEditable={user && !("error" in user) && user.admin}
            initialUnidades={_zas.uniSensivel}
            onChange={handleSelectUnidadesSensiveis}
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions>
        <Button onClick={() => setOpenUniSensivelTable(false)}>
          Finalizar
        </Button>
      </DialogActions>
    </Dialog>

    {/* Sirenes na ZAS */}
    {_zas.sirenes && !("error" in _zas.sirenes) &&
      <Dialog
        fullWidth
        maxWidth={false}
        open={openSirenesTable}
        onClose={() => setOpenSirenesTable(false)}
      >
        <DialogContent>
          <Box sx={{
            minHeight: "75vh",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}>
            <SirenesTable
              isAdd isEditable
              initSirenes={_zas.sirenes}
              onChange={handleSelectSirenes}
            />
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button onClick={() => setOpenSirenesTable(false)}>
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>
    }

    {/* Pontos de Encontro */}
    {_zas.pontosEncontro && !("error" in _zas.pontosEncontro) &&
      <Dialog
        fullWidth
        open={openPontosEncontroTable}
        maxWidth={false}
        onClose={() => setOpenPontosEncontroTable(false)}
      >
        <DialogContent>
          <Box sx={{
            height: "100%",
            display: "flex",
            minHeight: "75vh",
            flexDirection: "column",
          }}>
            <PontosEncontroTable
              isAdd isEditable
              initPontos={_zas.pontosEncontro}
              onChange={handlePontosEncontro}
            />
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button onClick={() => setOpenPontosEncontroTable(false)}>
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>
    }

    {/* Cadastros na ZAS */}
    {_zas.cadastros && !("error" in _zas.cadastros) &&
      <Dialog
        fullWidth
        maxWidth={false}
        open={openCadastrosTable}
        onClose={() => setOpenCadastrosTable(false)}
      >
        <DialogContent>
          <Box sx={{
            minHeight: "75vh",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}>
            <CadastrosTable
              isAdd isEditable
              initCadastros={_zas.cadastros}
              onChange={handleCadastros}
            />
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button onClick={() => setOpenCadastrosTable(false)}>
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>
    }

    {/* Matches das Rotas de Fuga */}
    <MatchProperties
      open={Boolean(propertiesRotasFuga)}
      onClose={() => setPropertiesRotasFuga(undefined)}
      onFinish={handleRotasDeFuga}
      description="Selecione as colunas das geometrias correspondentes a cada atributo. (* Atributo obrigatório)"
      maxWidth="md"
      deseredProperties={{
        "* Nome": "string",
        "* Nível de Validade": "number",
        "* Tempo de Chegada": "number",
        "Tempo de Saída": "number",
        "Comprimento da Rota": "number",
        "Observações": "string",
      }}
      properties={propertiesRotasFuga ?? {}}
    />

    {/* Matches das Rotas Alternativas */}
    <MatchProperties
      open={Boolean(propertiesRotasAlternativas)}
      onClose={() => setPropertiesRotasAlternativas(undefined)}
      onFinish={handleRotasAlternativas}
      description="Selecione as colunas das geometrias correspondentes a cada atributo."
      maxWidth="md"
      deseredProperties={{
        "Nome": "string",
        "Distância Percorrida": "number",
        "Tempo de Deslocamento": "number",
      }}
      properties={propertiesRotasAlternativas ?? {}}
    />

    {/* Lista de dados */}
    <Box sx={{
      gap: "15px",
      width: "100%",
      padding: "15px",
      display: "flex",
      flexDirection: "column",
    }}>

      {/* DropDown */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gapRow: "15px",
          cursor: "pointer",
        }}>

        {/* Dropdown Button */}
        <Tooltip title={showEstruturas ? "Esconder ZAS" : "Mostrar ZAS"}>
          <IconButton onClick={() => setShowEstruturas(!showEstruturas)}>
            {showEstruturas ? <ArrowDropUp /> : <ArrowDropDown />}
          </IconButton>
        </Tooltip>

        <Typography
          fontWeight={"bold"}
          fontSize={18}
          onClick={() => setShowEstruturas(!showEstruturas)}
          sx={{
            cursor: "pointer",
          }}
        >
          Zona de Autossalvamento e Salvamento Secundário
        </Typography>

        <div
          onClick={() => setShowEstruturas(!showEstruturas)}
          style={{
            flex: 1,
            height: "26px",
            cursor: "pointer",
          }}
        />
      </Box>

      {showEstruturas && <Box sx={{
        gap: "15px",
        width: "100%",
        display: "flex",
        flexDirection: "row",
      }}>

        {/* Dados */}
        <Box sx={{
          flex: 1,
          gap: "15px",
          display: "flex",
          flexDirection: "column",
        }}>

          {/* Centro do município */}
          <Typography>Centro do Município</Typography>
          <Box sx={{
            gap: "15px",
            width: "100%",
            display: "flex",
            flexDirection: "row",
          }}>
            {/* Latitude */}
            <TextField
              fullWidth
              label="Latitude"
              name="lat"
              value={centerGeom[0].toString().replaceAll(".", ",")}
              onChange={(value) => {
                const strVal = value.target.value.replaceAll(",", ".");

                let val: number | string = strVal;
                if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                  val = Number(strVal);
                  if (isNaN(val)) return;
                }
                setCenterGeom([val, centerGeom[1]]);
              }}
              variant="outlined"
            />

            {/* Longitude */}
            <TextField
              fullWidth
              label="Longitude"
              name="lon"
              value={centerGeom[1].toString().replaceAll(".", ",")}
              onChange={(value) => {
                const strVal = value.target.value.replaceAll(",", ".");

                let val: number | string = strVal;
                if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                  val = Number(strVal);
                  if (isNaN(val)) return;
                }
                setCenterGeom([centerGeom[0], val]);
              }}
              variant="outlined"
            />

          </Box>
          <Divider />

          {/* Distâncias Máximas */}
          <Box sx={{
            gap: "15px",
            width: "100%",
            display: "flex",
            flexDirection: "row",
          }}>
            {/* Dist Max Sirenes */}
            <TextField
              fullWidth
              label="Dist. máx. entre sirenes (m)"
              name="distMaxSirenes"
              value={_zas.distMaxSirenes?.toString().replaceAll(".", ",")}
              onChange={(value) => {
                value.target.value = value.target.value.replaceAll(",", ".");
                let val: undefined | number = Number(value.target.value);
                if (isNaN(val)) val = undefined;
                setZas({ ..._zas, distMaxSirenes: val });
              }}
              variant="outlined"
            />

            {/* Dist Max Percorrida */}
            <TextField
              fullWidth
              label="Dist. máx. percorrida na rota de fuga (m)"
              name="distMaxSirenes"
              value={_zas.distMaxPercorrida?.toString().replaceAll(".", ",")}
              onChange={(value) => {
                value.target.value = value.target.value.replaceAll(",", ".");
                let val: undefined | number = Number(value.target.value);
                if (isNaN(val)) val = undefined;
                setZas({ ..._zas, distMaxPercorrida: val });
              }}
              variant="outlined"
            />

          </Box>
          <Divider />

          {/* Botões de opções */}
          <Box sx={{ width: '100%', height: "100%" }}>
            <Grid2 container spacing={2}>

              {/* ZAS */}
              <Grid2 size={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    const layers: HidroLayer[] = [];

                    layers.push(new HidroLayer({
                      name: "Mapa",
                      showing: true,
                      layer: googleSateliteHybrid(),
                    }));

                    if (_zas.polygon && !("error" in _zas.polygon)) {
                      layers.push(new HidroLayer({
                        name: "ZAS",
                        showing: true,
                        layer: _zas.polygon,
                      }));
                    }

                    setGeomEditing(new HidroLayerGroup({
                      name: "Layers",
                      showing: true,
                      layers: layers,
                    }));
                    setTimeout(() => setSelectGeomType("poly_zas"));
                  }}
                >
                  Polígono da ZAS
                </Button>
              </Grid2>

              {/* ZSS */}
              <Grid2 size={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const layers: HidroLayer[] = [];

                    layers.push(new HidroLayer({
                      name: "Mapa",
                      showing: true,
                      layer: googleSateliteHybrid(),
                    }));

                    if (_zas.polygonZss && !("error" in _zas.polygonZss)) {

                      layers.push(new HidroLayer({
                        name: "ZSS",
                        showing: true,
                        layer: _zas.polygonZss,
                      }));
                    }

                    setGeomEditing(new HidroLayerGroup({
                      name: "Layers",
                      showing: true,
                      layers: layers,
                    }));
                    setTimeout(() => setSelectGeomType("poly_zss"));
                  }}
                >
                  Polígono da ZSS
                </Button>
              </Grid2>

              {/* Unidades Sensíveis */}
              <Grid2 size={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setOpenUniSensivelTable(true)}
                >
                  Unidades Sensíveis
                </Button>
              </Grid2>

              {/* Rotas de Fuga */}
              <Grid2 size={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const layers: (HidroLayer | HidroLayerGroup)[] = [];

                    layers.push(new HidroLayer({
                      name: "Mapa",
                      showing: true,
                      layer: googleSateliteHybrid(),
                    }));

                    if (_zas.polygon && !("error" in _zas.polygon)) {
                      layers.push(new HidroLayer({
                        name: "ZAS",
                        showing: true,
                        layer: _zas.polygon,
                        properties: _zas.polygon.properties
                      }));
                    }

                    if (_zas.rotasFuga && !("error" in _zas.rotasFuga)) {
                      layers.push(new HidroLayerGroup({
                        name: "Rotas de Fuga",
                        showing: true,
                        layers: _zas.rotasFuga.map((rota) => new HidroLayer({
                          name: rota.properties.nome,
                          showing: true,
                          layer: rota,
                          properties: rota.properties,
                        })),
                      }));
                    }

                    setGeomEditing(new HidroLayerGroup({
                      name: "Layers",
                      showing: true,
                      layers: layers,
                    }));
                    setTimeout(() => setSelectGeomType("rota_fuga"));
                  }}
                >
                  Rotas de Fuga
                </Button>
              </Grid2>

              {/* Rotas Alternativas */}
              <Grid2 size={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {

                    const layers: (HidroLayer | HidroLayerGroup)[] = [];

                    layers.push(new HidroLayer({
                      name: "Mapa",
                      showing: true,
                      layer: googleSateliteHybrid(),
                    }));

                    if (_zas.polygon && !("error" in _zas.polygon)) {
                      layers.push(new HidroLayer({
                        name: "ZAS",
                        showing: true,
                        layer: _zas.polygon,
                        properties: _zas.polygon.properties
                      }));
                    }

                    if (_zas.rotasAlternativas && !("error" in _zas.rotasAlternativas)) {
                      layers.push(new HidroLayerGroup({
                        name: "Rotas Alternativas",
                        showing: true,
                        layers: _zas.rotasAlternativas.map((rota) => new HidroLayer({
                          name: rota.properties.nome,
                          showing: true,
                          layer: rota,
                          properties: rota.properties,
                        })),
                      }));
                    }

                    setGeomEditing(new HidroLayerGroup({
                      name: "Layers",
                      showing: true,
                      layers: layers,
                    }));
                    setTimeout(() => setSelectGeomType("rotas_alternativas"));
                  }}
                >
                  Rotas Alternativas
                </Button>
              </Grid2>

              {/* Cadastro */}
              <Grid2 size={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setOpenCadastrosTable(true)}
                >
                  Cadastro
                </Button>
              </Grid2>

              {/* Pontos de Encontro */}
              <Grid2 size={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setOpenPontosEncontroTable(true)}
                >
                  Pontos de Encontro
                </Button>
              </Grid2>

              {/* Sirene */}
              <Grid2 size={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setOpenSirenesTable(true)}
                >
                  Sirenes
                </Button>
              </Grid2>

              {/* Acomodações */}
              <Grid2 size={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setShowAcomodacoes(true)}
                >
                  Acomodações
                </Button>
              </Grid2>

            </Grid2>
          </Box>
          <Divider />

          {/* Botão de Salvar */}
          <BeforeUnload>
            <Button
              variant="contained"
              onClick={handleChange}
              endIcon={<Save />}
            >
              Salvar Alterações
            </Button>
          </BeforeUnload>

        </Box>

        {/* Mapa */}
        <Box sx={{ flex: 0.7 }}>
          <HidroMap
            key={mapKey}
            recenter
            animateStart={false}
            hidroLayers={layers.current}
          />
        </Box>

      </Box>}

    </Box >
  </>;
};

function getUnidadeSensivelLayer(unidades: UnidadeSensivel[]) {
  return new HidroLayerGroup({
    name: "Unidades Sensíveis",
    showing: true,
    clusterize: unidades.length > 100,
    clusterizeOptions: {
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const icon = <Badge badgeContent={count}>
          <AddHomeWork />
        </Badge>;

        return divIcon({
          html: window.JSXToHTML(icon),
          className: 'custom-cluster-icon',
          iconSize: point(40, 40, true),
        });

      }
    },
    layers: unidades.map((unidade) => new HidroLayer({
      showing: true,
      layer: unidade,
      name: unidade.properties.nome,
      style: unidade.properties.style,
      properties: unidade.properties,
    })),
  });
}